import { Button, Form, MenuProps, message, PaginationProps, Select, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { AxiosError, AxiosResponse } from "axios";
import { debounce, join, trim } from "lodash";
import moment from "moment";
import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UploadIcon } from "../../assets/icons";
import ConfirmPopup from "../../commons/components/ConfirmPopup/ConfirmPopup";
import CustomInput from "../../commons/components/CustomInput/CustomInput";
import CustomModal from "../../commons/components/CustomModal/CustomModal";
import CustomPagination from "../../commons/components/CustomPagination";
import { CustomSelect } from "../../commons/components/CustomSelect/CustomSelect";
import CustomTable from "../../commons/components/CustomTable";
import CustomTabs from "../../commons/components/CustomTabs";
import GradientText from "../../commons/components/GradientText/GradientText";
import PrimaryButton from "../../commons/components/PrimaryButton/PrimaryButton";

import { StoreExample } from "../../commons/constants";
import SoldOutFromPopup from "./SoldOutFormPopup/SoldOutFromPopup";
import {
  CLONE_MERCHANTS_ALL_PRODUCTS_URL,
  CLONE_MERCHANTS_MULTI_PRODUCTS_URL,
  EXPORT_ATTRIBUTES_BY_STORE,
  EXPORT_PRODUCTS_BY_STORE,
  MARKUP_FEES,
  MERCHANT_ACOUNT_INFORMATION,
  MERCHANTS_PRODUCTS_APPROVAL_URL,
  MERCHANTS_PRODUCTS_URL,
  PRODUCT_MERCHANT_STORE_SETTINGS_URL,
  PUBLISH_DRAFT_PRODUCT_URL,
  UPDATE_PRODUCT_APPROVAL_URL,
  UPDATE_PRODUCT_PRICE,
  UPDATE_PRODUCT_URL,
  UPLOAD_PRODUCT_CSV,
  UPLOAD_PRODUCT_JSON,
} from "../../commons/constants/api-urls";
import { ProductStatusEnum } from "../../commons/constants/product";
import { routerNavigation } from "../../commons/constants/routers";
import { STORE_STATUS } from "../../commons/constants/store";
import useFetchList from "../../commons/hooks/useFetchList";
import useToast from "../../commons/hooks/useToast";
import { EditIcon, TrashIcon } from "../../commons/resources";
import defaultAxios, { uploadAxios } from "../../commons/utils/axios";
import { downloadCSV } from "../../commons/utils/functions/downloadCsv";
import { format2Digit } from "../../commons/utils/functions/formatPrice";
import { AppContext } from "../../contexts/AppContext";
import CustomIconStar from "../CustomIconStar";
import { ApprovalStatus, ProductActionType } from "../Form/CreateUpdateProductForm/CreateUpdateProductApprovalForm";
import styles from "./product-management.module.scss";
import { MerchantStatusEnum } from "../../commons/constants/user";
import { STATUS_CODE } from "../Notification/Notification";
import ImageDisplay from "../../commons/components/ImageDisplay";
import Icon from "../../assets/images/Frame 1000003923.svg";
import { calculateCostPrice } from "../../commons/utils/functions/productMapping";
import { CustomStoreSelect } from "../../commons/components/CustomStoreSelect/CustomStoreSelect";
import { STORE_TYPE_TABS } from "../StoreManagement";

interface DataType {
  key: React.Key;
  name: string;
  id: number;
  quantity: number;
  total_sales: number;
  price: number;
  images: string[];
  isFeatured: boolean;
  items: {
    id: number;
    name: string;
    description: string | null;
    attribute_name: string;
    attribute_value: string;
    price: number;
    sku: string | null;
    isSoldOut: boolean;
    minimumQuantityPerProductBulkThreshold: number;
  }[];
}

interface DataTypeApproval {
  key: React.Key;
  name: string;
  id: number;
  product_id: number | string;
  total_sales: number;
  description: string;
  type: string;
  update_time: string;
  price: number;
  status: string;
  images: string[];
  isFeatured: boolean;
}

export interface ProductItemApproval {
  id: number;
  type: string;
  update_time: string;
  price: number;
  description: string;
  approval_status: ApprovalStatus;
  product?: Product;
  payload: CreateUpdateProductBody;
  store: Store;
  create_time: string;
}

interface DataTypeProduct {
  key: React.Key;
  id: number;
  name: string;
}

const params = { perPage: 10, paginationMetadataStyle: "body", type: "retail,food" };

const ProductManagement: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { currentStore, findAndSetStore, refreshAll, handleFindStore } = useContext(AppContext);
  const [status, setStatus] = useState<string>(ProductStatusEnum.ACTIVE.toString());
  const { storeId } = useParams<{ storeId: string }>();
  const [activeTab, setActiveTab] = useState(ProductStatusEnum.ACTIVE.toString());
  const [perPage, setPerPage] = useState(10);
  const [keyword, setKeyword] = useState("");
  const [text, setText] = useState("");
  const [keywordExport, setKeywordExport] = useState("");

  const toast = useToast();
  const [showNote, setShowNote] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number[]>([]);
  const [markup, setMarkup] = useState(0);
  const [dataMerchant, setDataMerchant] = useState<any>();
  // fetch merchant data
  const getMerchantInfo = async () => {
    try {
      const res = await defaultAxios.get(MERCHANT_ACOUNT_INFORMATION());
      if (res.data && res.status === STATUS_CODE.SUCCESS) {
        setDataMerchant(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    getMerchantInfo();
  }, []);
  // fetch markup
  async function handleGetMarkup() {
    try {
      const res = await defaultAxios.get(MARKUP_FEES(currentStore?.id));
      setMarkup(res.data.mark_up);
    } catch (error) {
      message.error("Get markup failed");
    }
  }
  useEffect(() => {
    if (currentStore.id !== StoreExample.id) {
      handleGetMarkup();
    }
  }, [currentStore]);

  const {
    data: productsActive,
    total: totalActive,
    refresh: refreshActive,
  } = useFetchList<ProductItem>(currentStore ? MERCHANTS_PRODUCTS_URL(currentStore.id) : "", {
    ...params,
    page,
    status:
      dataMerchant?.information?.status?.toString() === MerchantStatusEnum.DRAFT.toString()
        ? ProductStatusEnum.PENDING_FOR_REVIEW
        : ProductStatusEnum.ACTIVE,
    keyword: keyword.trim(),
    perPage,
    sort_by: "create_time",
    order_by: "DESC",
  });

  const {
    data: productsDraft,
    total: totalDraft,
    refresh: refreshDraft,
  } = useFetchList<ProductItem>(currentStore ? MERCHANTS_PRODUCTS_URL(currentStore.id) : "", {
    ...params,
    page,
    status: ProductStatusEnum.DRAFT,
    keyword: keyword.trim(),
    perPage,
    sort_by: "create_time",
    order_by: "DESC",
  });

  const {
    data: productApprovals,
    total: totalApproval,
    refresh: refreshApproval,
  } = useFetchList<ProductItemApproval>(currentStore ? MERCHANTS_PRODUCTS_APPROVAL_URL(currentStore.id) : "", {
    ...params,
    page,
    status: `${ApprovalStatus.PENDING}`,
    keyword: keyword.trim(),
    perPage,
    sort_by: "create_time",
    order_by: "DESC",
  });

  const {
    data: productRejects,
    total: totalReject,
    refresh: refreshReject,
  } = useFetchList<ProductItemApproval>(currentStore ? MERCHANTS_PRODUCTS_APPROVAL_URL(currentStore.id) : "", {
    ...params,
    page,
    status: `${ApprovalStatus.REJECT}`,
    keyword: keyword.trim(),
    perPage,
    sort_by: "create_time",
    order_by: "DESC",
  });

  const paramsExport = { page: 1, perPage: 1000, paginationMetadataStyle: "body" };
  const [openModalExport, setOpenModalExport] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRowDraftKeys, setSelectedRowDraftKeys] = useState<React.Key[]>([]);
  const [selectedId, setSelectedId] = useState<number[]>([]);
  const [selectedDraftId, setSelectedDraftId] = useState<number[]>([]);

  const { data: productsExport } = useFetchList<ProductItem>(
    currentStore && openModalExport ? MERCHANTS_PRODUCTS_URL(currentStore.id) : "",
    {
      ...paramsExport,
      status,
      keyword: keywordExport.trim(),
      sort_by: "create_time",
      order_by: "DESC",
    }
  );

  const dataExport: DataTypeProduct[] = useMemo(() => {
    if (productsExport) {
      return productsExport.map((value, index) => ({
        key: value.id,
        id: value.id,
        name: value.name,
      }));
    }
    return [];
  }, [productsExport]);

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: DataTypeProduct[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedId(selectedRows.map(value => value.id));
  };

  const onSelectChangeDraft = (newSelectedRowKeys: React.Key[], selectedRows: DataType[]) => {
    setSelectedRowDraftKeys(newSelectedRowKeys);
    setSelectedDraftId(selectedRows.map(value => value.id));
  };

  const rowSelectionExport = {
    selectedRowKeys,
    onChange: onSelectChange,

    getCheckboxProps: (record: DataTypeProduct) => ({
      name: record.name.toString(),
    }),
  };

  const columnsExport: ColumnsType<DataTypeProduct> = [
    {
      title: "All product",
      dataIndex: "name",
      render: (_, record) => {
        return <div>{record.name}</div>;
      },
    },
  ];

  const [disableUpload, setDisableUpload] = useState(true);
  const [thumbnail, setThumbnail] = useState<string>("Drag & drop here or browse file");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [uploadType, setUploadType] = useState<string>("json");

  const [loading, setLoading] = useState(false);
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [openConfirmDeleteApproval, setOpenConfirmDeleteApproval] = useState(false);
  const [openConfirmPopupSoldOut, setOpenConfirmPopupSoldOut] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [selectedProductApproval, setSelectedProductApproval] = useState<DataTypeApproval>();
  const [form] = Form.useForm<{ merchant_store_ids: string[] }>();

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    setPerPage(pageSize || 10);
  };

  const [openModal, setOpenModal] = useState(false);
  const [openModalUpload, setOpenModalUpload] = useState(false);

  const deleteProduct = async (productId: string | number) => {
    try {
      const res: AxiosResponse<any> = await defaultAxios.delete(UPDATE_PRODUCT_URL(productId));
      if (!!res?.data) {
        message.success("Delete product successfully");
        refreshActive();
        refreshDraft();
      }
    } catch (error) {
      console.log("delete product error", error);
      message.error("Delete product failed");
    }
  };

  const deleteProductApproval = async (approvalId: string | number) => {
    try {
      const res: AxiosResponse<any> = await defaultAxios.put(UPDATE_PRODUCT_APPROVAL_URL(approvalId), {
        approval_status: ApprovalStatus.DELETE,
      });
      if (!!res?.data) {
        message.success("Delete approval product successfully");
        if (activeTab === ProductStatusEnum.REJECTED.toString()) {
          refreshReject();
        } else {
          refreshApproval();
        }
      }
    } catch (error) {
      console.log("delete approval product error", error);
      message.error("Delete product approval failed");
    }
  };

  // const markProductSoldOut = async (product: Product) => {
  //   try {
  //     await defaultAxios.patch(MARK_PRODUCT_SOLD_OUT(product.id), { is_sold_out: !product.is_sold_out });
  //     message.success("Mark product sold out successfully");
  //     refreshActive();
  //   } catch (error) {
  //     console.log(error);
  //     message.error("Mark product sold out failed");
  //   }
  // };

  const featuredProduct = async (productId: string | number, value: boolean) => {
    try {
      const res = await defaultAxios.put(PRODUCT_MERCHANT_STORE_SETTINGS_URL(productId), {
        is_featured: !value,
      });
      if (res) {
        refreshActive();
      }
    } catch (error) {
      console.log("featured product error", error);
      message.error("featured product failed");
    }
  };

  const handleDeleteProduct = (product: Product) => {
    setSelectedProduct(product);
    setOpenConfirmPopup(true);
  };

  const handleDeleteProductApproval = (product: DataTypeApproval) => {
    setSelectedProductApproval(product);
    setOpenConfirmDeleteApproval(true);
  };

  const handleMarkProductSoldOut = async (product: Product) => {
    setSelectedProduct(product);
    setOpenConfirmPopupSoldOut(true);
  };

  const handleTabChange = (key: string) => {
    setStatus(key);
    setActiveTab(key);
    setSelectedProductId([]);
    if (key === ProductStatusEnum.ACTIVE.toString()) {
      refreshActive();
    } else if (key === ProductStatusEnum.DRAFT.toString()) {
      refreshDraft();
    } else if (key === ProductStatusEnum.REJECTED.toString()) {
      refreshReject();
    } else {
      refreshApproval();
    }
  };

  function handleOpenAdminNote(id: number) {
    setShowNote(prev => (prev === id ? null : id));
  }

  useEffect(() => {
    if (!storeId) return;
    findAndSetStore(storeId);
  }, [storeId]);

  const handleClickStore = async (values: any) => {
    if (loading) return;
    try {
      setLoading(true);
      // let body = {
      //   merchant_store_ids: values.merchant_store_ids,
      // };
      // const response = await defaultAxios.post(`${CLONE_MERCHANTS_PRODUCTS_URL(selectProductId)}`, body);
      const body: any = {
        product_ids: selectedProductId,
        merchant_store_ids: values.merchant_store_ids,
      };

      let response;
      if (selectedProductId.length === 0) {
        delete body.product_ids;
        response = await defaultAxios.post(CLONE_MERCHANTS_ALL_PRODUCTS_URL(currentStore.id), body);
      } else {
        response = await defaultAxios.post(CLONE_MERCHANTS_MULTI_PRODUCTS_URL(), body);
      }

      if (!!response?.data) {
        const arrName = values.merchant_store_ids?.map((item: string) => handleFindStore(item)) || [];

        const storeName = join(arrName, " , ");
        setOpenModal(false);
        form.resetFields();
        refreshDraft();
        refreshAll();
        setLoading(false);
        setSelectedProductId([]);
        setSelectedRowKeys([]);
        message.success(`Product cloned to ${storeName} as draft.`);
      } else {
        setLoading(false);
        message.error("Clone product failed");
      }
    } catch (error) {
      setLoading(false);
      message.error("Clone product failed");
    }
  };

  const handleCancel = () => {
    setOpenModal(false);
    form.resetFields();
  };

  const handleCancelUpload = () => {
    setFile(undefined);
    setOpenModalUpload(false);
  };

  const handleCancelExport = () => {
    setOpenModalExport(false);
  };

  const Tabs = [
    {
      title: currentStore.status === STORE_STATUS.DRAFT ? "Approved" : "Published",
      key: ProductStatusEnum.ACTIVE.toString(),
      children: <span>({totalActive})</span>,
    },

    {
      title: "Draft",
      key: ProductStatusEnum.DRAFT.toString(),
      children: <span> ({totalDraft})</span>,
    },
    {
      title: "Waiting approval",
      key: ApprovalStatus.PENDING.toString(),
      children: <span>({totalApproval})</span>,
    },
    {
      title: "Rejected",
      key: ProductStatusEnum.REJECTED.toString(),
      children: <span>({totalReject})</span>,
    },
  ];

  const dataActive: DataType[] = useMemo(() => {
    return productsActive.map((product: any) => {
      return {
        key: product.id,
        name: product.name,
        images: product.images.length > 0 ? product.images : [Icon],
        id: product.id,
        quantity: product.quantity,
        total_sales: product.total_sales,
        price: product.price,
        isFeatured: product.settings["is_featured"],
        is_sold_out: product.is_sold_out,
        isPreviewing: product.isPreviewing,
        items: product.items,
      };
    });
  }, [productsActive]);

  const dataDraft: DataType[] = useMemo(() => {
    return productsDraft.map((product: any) => {
      return {
        key: product.id,
        name: product.name,
        images: product.images.length > 0 ? product.images : [Icon],
        id: product.id,
        quantity: product.quantity,
        total_sales: product.total_sales,
        price: product.price,
        isFeatured: product.settings["is_featured"],
        items: product.items,
      };
    });
  }, [productsDraft]);

  const dataApproval: DataTypeApproval[] = useMemo(() => {
    return productApprovals.map((approval: ProductItemApproval) => {
      const name =
        approval.type === ProductActionType.PUBLISH
          ? approval.product
            ? approval.product.name
            : ""
          : approval.payload.name;
      const images =
        approval.type === ProductActionType.PUBLISH
          ? approval.product
            ? approval.product.images
            : []
          : approval.payload.images;
      return {
        key: approval.id,
        name: name,
        images: images.length > 0 ? images : [Icon],
        id: approval.id,
        product_id: approval.product ? approval.product.id : 0,
        description: approval.description,
        update_time: moment(approval.update_time).format("DD/MM/yyyy HH:mm"),
        type: approval.type,
        status:
          approval.approval_status === ApprovalStatus.APPROVE
            ? "Approved"
            : approval.approval_status === ApprovalStatus.PENDING
            ? "Pending"
            : "Rejected",
        quantity: 0,
        total_sales: approval.product ? (approval.product.total_sales ? approval.product.total_sales : 0) : 0,
        price: approval.price,
        isFeatured: approval.payload
          ? approval.payload.settings
            ? approval.payload?.settings["is_featured"]
            : false
          : false,
      };
    });
  }, [productApprovals]);

  const dataReject: DataTypeApproval[] = useMemo(() => {
    return productRejects.map((reject: ProductItemApproval) => {
      const name =
        reject.type === ProductActionType.PUBLISH ? (reject.product ? reject.product.name : "") : reject.payload.name;
      const images =
        reject.type === ProductActionType.PUBLISH
          ? reject.product
            ? reject.product.images
            : []
          : reject.payload.images;
      return {
        key: reject.id,
        name: name,
        images: images,
        id: reject.id,
        product_id: reject.product ? reject.product.id : 0,
        description: reject.description,
        update_time: moment(reject.update_time).format("DD/MM/yyyy HH:mm"),
        type: reject.type,
        status: "Rejected",
        quantity: 0,
        total_sales: reject.product ? (reject.product.total_sales ? reject.product.total_sales : 0) : 0,
        price: reject.price,
        isFeatured: reject.payload
          ? reject.payload.settings
            ? reject.payload?.settings["is_featured"]
            : false
          : false,
      };
    });
  }, [productRejects]);

  const rowSelectionDraft = {
    selectedRowDraftKeys,
    onChange: onSelectChangeDraft,
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Featured",
      width: 100,
      render: (_, record: DataType) => {
        return <CustomIconStar isFeatured={record.isFeatured} onClick={featuredProduct} id={record.id} />;
      },
    },
    {
      title: "Product name",
      dataIndex: "name",
      // width: 300,
      render: (_, record) => {
        return (
          <div
            style={{ width: "200px" }}
            className={`${styles.product} ${styles.pointer}`}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_PRODUCT(currentStore?.id || "", record.id))}
          >
            <ImageDisplay src={record.images[0]} alt={record.name} />
            <div className={styles.productName}>{record.name}</div>
          </div>
        );
      },
    },
    {
      title: "Product ID",
      dataIndex: "id",
      render: (text, record) => {
        return (
          <div
            style={{ width: "80px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_PRODUCT(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: "Total sold",
      dataIndex: "total_sales",
      render: (text, record) => {
        return (
          <div
            style={{ width: "70px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_PRODUCT(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            {text || 0}
          </div>
        );
      },
    },
    {
      title: "Cost",
      dataIndex: "price",
      render: (text, record) => {
        return (
          // <Input
          //   defaultValue={((text / (100 + markup)) * 100).toFixed(2)}
          //   style={{ width: "100px", backgroundColor: "#212124", color: "white" }}
          //   onChange={e => debouncedEditProductPrice(e, record, "cost")}
          //   type="number"
          //   onKeyDown={event => {
          //     decimalOnlyInput(event);
          //   }}
          //   readOnly
          // />
          <div
            style={{ width: "70px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_PRODUCT(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            {/* {((text / (100 + markup)) * 100).toFixed(2)} */}
            {calculateCostPrice(text, markup)}
          </div>
        );
      },
    },
    {
      title: "Selling price",
      dataIndex: "price",
      render: (text, record) => {
        return (
          // <Input
          //   defaultValue={format2Digit(text)}
          //   style={{ width: "100px", backgroundColor: "#212124", color: "white" }}
          //   onChange={e => debouncedEditProductPrice(e, record, "selling")}
          //   type="number"
          //   onKeyDown={event => {
          //     decimalOnlyInput(event);
          //   }}
          //   readOnly
          // />
          <div
            style={{ width: "70px" }}
            onClick={() => navigate(routerNavigation.STORES.UPDATE_PRODUCT(currentStore?.id || "", record.id))}
            className={styles.pointer}
          >
            {format2Digit(text)}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (row: Product) => (
        <>
          <div className={styles.actions}>
            {/* <div className={styles.edit}>
              <CloneIcon
                onClick={() => {
                  setSelectProductId(row.id);
                  setOpenModal(true);
                }}
              />
            </div> */}
            <div className={styles.edit}>
              <EditIcon
                onClick={() => navigate(routerNavigation.STORES.UPDATE_PRODUCT(currentStore?.id || "", row.id))}
              />
            </div>
            <div className={styles.delete} onClick={() => handleDeleteProduct(row)}>
              <TrashIcon />
            </div>
            {activeTab === ProductStatusEnum.ACTIVE.toString() && (
              <div
                style={{ width: "70px" }}
                className={`${styles.soldOut} ${row.is_sold_out && styles.true}`}
                onClick={() => handleMarkProductSoldOut(row)}
              >
                Sold out
              </div>
            )}
          </div>
        </>
      ),
    },
  ];

  const getUrl = (storeId: string, approvalId: number) => {
    return routerNavigation.STORES.UPDATE_PRODUCT_APPROVAL(storeId, approvalId);
  };

  const columnsApproval: ColumnsType<DataTypeApproval> = [
    {
      title: "Request ID",
      dataIndex: "id",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text ? text : ""}
          </div>
        );
      },
    },
    {
      title: "Product name",
      dataIndex: "name",
      render: (_, record) => {
        return (
          <div
            className={`${styles.product} ${styles.pointer}`}
            onClick={() => navigate(getUrl(currentStore?.id || "", record.id))}
          >
            <ImageDisplay src={record.images[0]} alt={record.name} />
            <div className={styles.productName}>{record.name}</div>
          </div>
        );
      },
    },
    {
      title: "Product ID",
      dataIndex: "product_id",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text ? text : ""}
          </div>
        );
      },
    },
    {
      title: "Unit price",
      dataIndex: "price",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            S$ {format2Digit(text)}
          </div>
        );
      },
    },
    {
      title: "Last Update",
      dataIndex: "update_time",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <>
          {(record.status === "Pending" || record.status === "Rejected") && (
            <div className={styles.actions}>
              <div className={styles.delete} onClick={() => handleDeleteProductApproval(record)}>
                <TrashIcon />
              </div>
            </div>
          )}
        </>
      ),
    },
  ];
  const columnsReject: ColumnsType<DataTypeApproval> = [
    {
      title: "Request ID",
      dataIndex: "id",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text ? text : ""}
          </div>
        );
      },
    },
    {
      title: "Product name",
      dataIndex: "name",
      render: (_, record) => {
        return (
          <div
            className={`${styles.product} ${styles.pointer}`}
            onClick={() => navigate(getUrl(currentStore?.id || "", record.id))}
          >
            <ImageDisplay src={record.images[0]} alt={record.name} />
            <div className={styles.productName}>{record.name}</div>
          </div>
        );
      },
    },
    {
      title: "Product ID",
      dataIndex: "product_id",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text ? text : ""}
          </div>
        );
      },
    },
    {
      title: "Unit price",
      dataIndex: "price",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            S$ {format2Digit(text)}
          </div>
        );
      },
    },
    {
      title: "Last Update",
      dataIndex: "update_time",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Admin Note",
      dataIndex: "description",
      render: (text, record) => {
        return (
          <div onClick={() => handleOpenAdminNote(record.id)} className={styles.pointer}>
            <span className={`${styles.adminNote} ${showNote === record.id && styles.noDot}`}> {text}</span>
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        return (
          <div onClick={() => navigate(getUrl(currentStore?.id || "", record.id))} className={styles.pointer}>
            {text}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <>
          {(record.status === "Pending" || record.status === "Rejected") && (
            <div className={styles.actions}>
              <div className={styles.delete} onClick={() => handleDeleteProductApproval(record)}>
                <TrashIcon />
              </div>
            </div>
          )}
        </>
      ),
    },
  ];

  const [file, setFile] = useState<File>();

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!file.name.endsWith(`.${uploadType}`)) {
        setErrorMessage(`Select file .${uploadType}`);
        e.target.value = "";
        return;
      } else {
        setFile(file);
        e.target.value = "";
        setErrorMessage("");
      }
    } else {
      toast.error("No file selected");
    }
  };

  useEffect(() => {
    if (file) {
      setThumbnail(file.name);
      setDisableUpload(false);
    } else {
      setThumbnail("Drag & drop here or browse file");
      setDisableUpload(true);
    }
  }, [file]);

  async function onClickExport() {
    const FileDownload = require("js-file-download");

    defaultAxios
      .post(
        `/merchant-management/merchant-stores/${currentStore?.id}/products/export`,
        { productIds: selectedId },
        { responseType: "blob" }
      )
      .then(response => {
        FileDownload(response.data, "product.json");
      });
  }

  async function onClickExportAllProducts() {
    try {
      // Fetch the CSV files
      const [csvProductsFile, csvAttributesFile] = await Promise.all([
        defaultAxios.get(EXPORT_PRODUCTS_BY_STORE(currentStore?.id || ""), { responseType: "blob" }),
        defaultAxios.get(EXPORT_ATTRIBUTES_BY_STORE(currentStore?.id || ""), { responseType: "blob" }),
      ]);
      // Download the files
      downloadCSV(csvProductsFile.data, `${currentStore?.name}_products.csv`);
      if (dataActive?.some(item => item?.items.length > 1)) {
        downloadCSV(csvAttributesFile.data, `${currentStore?.name}_attributes.csv`);
      }
    } catch (error) {
      console.log({ error });
    }
  }

  async function onClickUpload() {
    if (!file) {
      toast.error("No file selected");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await uploadAxios.post<FormData, AxiosResponse<ImageItem>>(
        uploadType === "json"
          ? UPLOAD_PRODUCT_JSON(currentStore ? currentStore.id : "")
          : UPLOAD_PRODUCT_CSV(currentStore ? currentStore.id : ""),
        formData
      );
      toast.success("Upload success !");
      refreshDraft();
      refreshAll();
      setUploading(false);
      setStatus(ProductStatusEnum.DRAFT.toString());
      setActiveTab(ProductStatusEnum.DRAFT.toString());
      setFile(undefined);
      setOpenModalUpload(false);
    } catch (err: any) {
      console.log(err);
      setFile(undefined);
      const error = (err as AxiosError<any>)?.response?.data;
      const message =
        typeof error?.error.message === "string"
          ? error?.error.message
          : typeof error?.error.message?.[0] === "string"
          ? error.error.message[0]
          : "Upload Fail";
      toast.error(message);
      setUploading(false);
    }
  }

  const downloadTemplateJSON = () => {
    const FileDownload = require("js-file-download");

    defaultAxios.get("/merchant-management/products/upload/template", { responseType: "blob" }).then(response => {
      FileDownload(response.data, "template.json");
    });
  };

  const downloadTemplateCSV = () => {
    const FileDownload = require("js-file-download");

    defaultAxios.get("/merchant-management/products/upload/csv/template", { responseType: "blob" }).then(response => {
      FileDownload(response.data, "template.csv");
    });
  };

  const items = [
    {
      label: "CSV Template",
      key: "csv",
    },
    {
      label: "JSON Template",
      key: "json",
    },
  ];

  const uploadOptions = [
    { value: "csv", label: "Csv" },
    { value: "json", label: "Json" },
  ];

  const handleMenuClick: MenuProps["onClick"] = e => {
    const key = e.key;
    if (key === "json") {
      downloadTemplateJSON();
    } else if (key === "csv") {
      downloadTemplateCSV();
    }
  };

  const handleChangeUploadOption = (value: string) => {
    setUploadType(value);
    setFile(undefined);
  };

  const clickPublish = async () => {
    try {
      if (selectedDraftId.length === 0) {
        message.error("No product selected !");
        return;
      }
      await defaultAxios.post(PUBLISH_DRAFT_PRODUCT_URL, { ids: selectedDraftId });
      message.success("Please wait for admin approval before proceeding");
      setSelectedDraftId([]);
      setSelectedRowDraftKeys([]);
      setActiveTab(ApprovalStatus.PENDING.toString());
      refreshApproval();
    } catch (error) {
      console.log(error);
      message.error("Publish product failed");
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setKeyword(text);
    }, 500);

    return () => clearTimeout(timeout);
  }, [text]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleTabTotalCounts = (event: any) => {
    setText(event.target.value.trim());
    refreshActive();
    refreshDraft();
    refreshApproval();
    refreshReject();
  };
  const getTotal = useMemo(() => {
    switch (activeTab) {
      case ProductStatusEnum.ACTIVE.toString():
        return totalActive;
      case ProductStatusEnum.DRAFT.toString():
        return totalDraft;
      case ApprovalStatus.PENDING.toString():
        return totalApproval;
      case ProductStatusEnum.REJECTED.toString():
        return totalReject;
    }
  }, [activeTab, totalActive, totalDraft, totalApproval, totalReject]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
      setSelectedProductId(selectedRows.map(item => item.id));
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  async function handleCloneStoreProduct() {
    setOpenModal(true);
  }

  return (
    <div className={styles.wrapper}>
      {uploading && (
        <div className={styles.loading}>
          <Spin size="large" />
        </div>
      )}
      <div className={styles.titleWrapper}>
        <div className={styles.title}>{`${trim(currentStore?.name)} - Product Management`}</div>
        {/* <div className={styles.uploadBtnWrapper}>
      <button className={styles.btn} onClick={() => setOpenModalUpload(true)}>
        Create product
      </button>
    </div> */}
        {/* 
    <Dropdown menu={menuProps} className={styles.downButton} trigger={["click"]}>
      <Button>
        <Space>
          Download Template <DownOutlined />
        </Space>
      </Button>
    </Dropdown> */}

        {/* <PrimaryButton
      style={{ marginRight: "15px" }}
      className={styles.addProductBtn}
      onClick={() => setOpenModalExport(true)}
    >
      Export product
    </PrimaryButton> */}

        <PrimaryButton
          style={{ marginRight: "15px" }}
          className={styles.addProductBtn}
          onClick={onClickExportAllProducts}
        >
          Export CSV
        </PrimaryButton>

        <PrimaryButton
          disabled={dataActive.length === 0}
          className={styles.cloneProductBtn}
          onClick={handleCloneStoreProduct}
        >
          {selectedProductId.length === 0
            ? "Clone all products"
            : `Clone ${selectedProductId.length} selected ${selectedProductId.length > 1 ? "products" : "product"}`}
        </PrimaryButton>

        <PrimaryButton
          className={styles.addProductBtn}
          onClick={() => navigate(routerNavigation.STORES.CREATE_PRODUCT(currentStore?.id || ""))}
        >
          Add new product
        </PrimaryButton>
      </div>
      <CustomInput
        onChange={handleTabTotalCounts}
        className={styles.inputSearch}
        type="search"
        placeholder="Search for product name"
      />
      <div className={styles.productPanel}>
        <CustomTabs
          tabStyle={styles.customTab}
          // className={"tabProduct"}
          activeTab={activeTab}
          onChange={handleTabChange}
          tabs={Tabs}
        >
          <div className={styles.tabContent}>
            {markup ? (
              <>
                {activeTab === ProductStatusEnum.ACTIVE.toString() && (
                  <CustomTable
                    pagination={false}
                    columns={columns}
                    dataSource={dataActive}
                    rowSelection={{
                      type: "checkbox",
                      ...rowSelection,
                    }}
                    scroll={{ x: 400 }}
                  />
                )}
              </>
            ) : undefined}

            {activeTab === ProductStatusEnum.DRAFT.toString() && (
              <CustomTable
                pagination={false}
                rowSelection={{
                  type: "checkbox",
                  ...rowSelectionDraft,
                }}
                scroll={{ x: 400 }}
                columns={columns.filter(item => item.title !== "Featured")}
                dataSource={dataDraft}
              />
            )}

            {activeTab === ApprovalStatus.PENDING.toString() && (
              <CustomTable pagination={false} columns={columnsApproval} dataSource={dataApproval} scroll={{ x: 400 }} />
            )}

            {activeTab === ProductStatusEnum.REJECTED.toString() && (
              <CustomTable pagination={false} columns={columnsReject} dataSource={dataReject} scroll={{ x: 400 }} />
            )}
          </div>
        </CustomTabs>
        <div className={styles.paginationWrapper}>
          <CustomPagination
            showLessItems={true}
            defaultCurrent={1}
            showSizeChanger
            current={page}
            onChange={page => setPage(page)}
            total={getTotal}
            onShowSizeChange={onShowSizeChange}
            pageSize={perPage}
          />
        </div>
        {activeTab === ProductStatusEnum.DRAFT.toString() && (
          <Button
            size={"large"}
            disabled={selectedDraftId.length === 0}
            onClick={clickPublish}
            className={`${styles.save} ${selectedDraftId.length === 0 ? styles.saveDisable : ""}`}
            style={{ marginTop: "20px" }}
          >
            Publish Product
          </Button>
        )}
      </div>
      <CustomModal
        maskClosable={false}
        classBottom={styles.bottomModal}
        footer={<></>}
        classTitle={styles.titleModalChooseStore}
        title="Choose store(s) to clone these products"
        open={openModal}
        onCancel={handleCancel}
      >
        <div className={styles.textAvailable}>Available stores</div>
        <Form form={form} onFinish={handleClickStore}>
          <CustomStoreSelect
            rules={[
              {
                required: true,
                message: "Please select store",
              },
            ]}
            mode="multiple"
            name="merchant_store_ids"
            storeType={STORE_TYPE_TABS.FOOD_DELIVERY}
          />
          <div className={styles.groupButton}>
            <Button className={styles.buttonText} type="text" onClick={handleCancel}>
              Cancel
            </Button>
            <button type="submit" className={[styles.buttonGreen, loading && styles.loading].join(" ")}>
              Clone as draft
            </button>
          </div>
        </Form>
      </CustomModal>

      <CustomModal
        maskClosable={false}
        classBottom={styles.bottomModal}
        footer={<></>}
        classTitle={styles.titleModalChooseStore}
        title="Create product"
        open={openModalUpload}
        onCancel={handleCancelUpload}
      >
        <div className={styles.uploadWrapper}>
          <Select
            defaultValue={uploadType}
            className="select-reconciliation"
            style={{ width: "100%" }}
            onChange={handleChangeUploadOption}
            options={uploadOptions}
          />

          <span className={styles.uploadTitle}>Create product from file</span>
          <div className={styles.inputArea}>
            <UploadIcon />
            {thumbnail ? thumbnail : "Drag & drop here or browse file"}
            <input onChange={handleFileChange} type="file" id="choose-file" />
          </div>
          {!!errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
        </div>

        <div className={styles.footer}>
          <div className={styles.cancelButton} onClick={handleCancelUpload}>
            <GradientText text={"Cancel"}></GradientText>
          </div>
          <PrimaryButton
            onClick={onClickUpload}
            disabled={disableUpload}
            className={`${styles.confirmButton} ${disableUpload ? styles.btnDisable : ""}`}
          >
            {"Upload"}
          </PrimaryButton>
        </div>
      </CustomModal>

      <CustomModal
        maskClosable={false}
        classBottom={styles.bottomModal}
        footer={<></>}
        classTitle={styles.titleModalChooseStore}
        title="Export product"
        open={openModalExport}
        onCancel={handleCancelExport}
      >
        <div className={styles.uploadWrapper}>
          <CustomInput
            onChange={event => setKeywordExport(event.target.value)}
            onBlur={event => setKeywordExport(event.target.value.trim())}
            className={styles.inputSearch}
            type="search"
            placeholder="Search for product name"
          />

          <CustomTable
            style={{ maxHeight: "400px", overflowY: "scroll" }}
            pagination={false}
            rowSelection={{
              type: "checkbox",
              ...rowSelectionExport,
            }}
            columns={columnsExport}
            dataSource={dataExport}
          />
        </div>

        <div className={styles.footerExport}>
          <Button
            onClick={() => {
              handleCancelExport();
              setSelectedRowKeys([]);
              setSelectedId([]);
            }}
            className={styles.cancel}
          >
            Cancel
          </Button>
          <Button
            type={"primary"}
            onClick={onClickExport}
            disabled={selectedId.length === 0}
            className={`${styles.save} ${selectedId.length === 0 ? styles.saveDisable : ""}`}
          >
            Export
          </Button>
        </div>
      </CustomModal>

      <SoldOutFromPopup
        open={openConfirmPopupSoldOut}
        onCancel={() => setOpenConfirmPopupSoldOut(false)}
        title={`Sold Out`}
        description={`Please select items that are out of stock in this product`}
        product={selectedProduct}
      />

      {/* <ConfirmPopup
    open={openConfirmPopupSoldOut}
    onCancel={() => setOpenConfirmPopupSoldOut(false)}
    title={`Mark product as ${!selectedProduct?.is_sold_out ? "sold out" : "available"}`}
    description={`Do you want to mark ${selectedProduct?.name} as ${
      !selectedProduct?.is_sold_out ? "sold out" : "available"
    } `}
    onConfirm={() => {
      markProductSoldOut(selectedProduct!);
      setOpenConfirmPopupSoldOut(false);
    }}
  /> */}

      <ConfirmPopup
        open={openConfirmPopup}
        onCancel={() => setOpenConfirmPopup(false)}
        title="Delete Product"
        description={`Do you want to delete ${selectedProduct?.name}?`}
        onConfirm={() => {
          deleteProduct(selectedProduct?.id || "");
          setOpenConfirmPopup(false);
        }}
      />

      <ConfirmPopup
        open={openConfirmDeleteApproval}
        onCancel={() => setOpenConfirmDeleteApproval(false)}
        title="Delete Product Approval Request"
        description={`Do you want to delete approval request ${selectedProductApproval?.id}?`}
        onConfirm={() => {
          deleteProductApproval(selectedProductApproval?.id || "");
          setOpenConfirmDeleteApproval(false);
        }}
      />
    </div>
  );
};

export default ProductManagement;
